/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  Tab,
  Tabs,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Checkbox,
  CircularProgress,
  Select,
  SelectItem,
  Pagination,
  PaginationItemType,
  cn,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { format, parseISO } from "date-fns";
import {
  BsDatabaseFill,
  BsFolderFill,
  BsPlus,
  BsChevronDown,
  BsThreeDotsVertical,
  BsChatLeftText,
  BsInfoCircle,
  BsLockFill,
} from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import { AiFillPauseCircle, AiFillPlayCircle } from "react-icons/ai";
import { BiSolidUpArrow } from "react-icons/bi";
import {
  MdOutlinePhoneCallback,
  MdOutlinePhoneForwarded,
  MdOutlineStarBorder,
  MdOutlineFormatListBulleted,
} from "react-icons/md";
import { GoPlus } from "react-icons/go";

import CallModal from "./CallModal";
import Modal from "../../components/Modal";
import SmartListCallPreview from "../../components/SmartListCallPreview";
import {
  AISuggestionIcon,
  ArrowTopRightIcon,
  ChevronIcon,
  GlassyBookmarkIcon,
} from "../../assets/icons/audio-icons";
import MFloatingComponent from "../../components/Kit/MFloatingComponent";
import GenericTable from "../../components/Kit/GenericTable";
import CustomCopyToClipboard from "../../components/Kit/CustomCopyToClipboard";
import ACircleUserDisplay from "../../components/Kit/ACircleUserDisplay";
import AStarGradeDisplay from "../../components/Kit/AStarGradeDisplay";

import myAxios from "../../utils/axiosWrapper";
import { auth } from "../../conf/firebase";
import getConfig from "../../conf/config";
import { fetchListData, fetchLists } from "../../redux/actions/listsActions";
import { calls_table_columns } from "../../constants/tableHeaders";
import { useAppContext } from "../../contexts/AppContext";
import {
  addSelectedCall,
  addSelectedCalls,
  removeSelectedCall,
  removeSelectedCalls,
  resetSelectedCalls,
} from "../../redux/actions";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import { formatCallDuration } from "../../utils/callsUtils";
import useWindowSize from "../../hooks/useWindowSize";
import LiveContentModalContent from "../live-content-search/LiveContentModalContent";
import CalendarDropdown from "../../components/CalendarDropdown";

const Lists = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let { listId } = useParams();

  const [width, _height] = useWindowSize();
  const isSmallScreen = width <= 1350;

  const filteredTableColumns = (
    isSmallScreen
      ? calls_table_columns.filter((column) => !column?.hideOnSmallScreens)
      : calls_table_columns
  ).map((column) => ({ ...column, label: column.label ? t(column.label) : column.label }));

  const {
    debouncedSearchText: searchText,
    setSearchText,
    setPreventSearchReset,
    sidebarAudioUrl,
    setSidebarAudioUrl,
    setSidebarCallId,
    sidebarIsAudioPlaying,
    setSidebarIsAudioPlaying,
    setSidebarAudioTime,
    sidebarAudioRef,
    setSidebarAutoPlay,
    setPopupAudioUrl,
    setPopupCallId,
    setPopupAudioTime,
    setPopupIsAudioPlaying,
    popupIsAudioPlaying,
    popupAudioTime,
    individualGrades,
    popupAudioSpeed,
    setSidebarAudioSpeed,
  } = useAppContext();

  const isListCallsDisplay = listId && listId.length > 0;

  const dispatch = useDispatch();

  const [sortDescriptor, setSortDescriptor] = useState({
    sortBy: "call_timestamp",
    direction: "desc",
  });

  const [listDataFetched, setListDataFetched] = useState({});

  const [isSmartNewListMenuOpen, setSmartNewListMenuOpen] = useState(false);
  const [querySmartList, setQuerySmartList] = useState("");
  const [maxCallSmartList, setMaxCallSmartList] = useState(50);
  const [isSmartListLoading, setIsSmartListLoading] = useState(false);
  const [smartListCalls, setSmartListCalls] = useState([]);
  const [smartListMeta, setSmartListMeta] = useState(null);
  const [smartListLoadingStep, setSmartListLoadingStep] = useState(null);
  const [excludedSmartListCallIds, setExcludedSmartListCallIds] = useState([]);
  const [smartListName, setSmartListName] = useState("");
  const [isSmartListPrivate, setIsSmartListPrivate] = useState(false);
  const [isCreatingSmartList, setIsCreatingSmartList] = useState(false);

  // Smart list date range - default to last 30 days
  const getDefaultSmartListDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      startDate: start,
      endDate: end,
    };
  };
  const [smartListDateRange, setSmartListDateRange] = useState(getDefaultSmartListDates());

  const onFetchListData = async () => {
    if (!listId) {
      return;
    }
    setListDataFetched({});
    // A 404 here means the list is gone or is someone else's private list.
    dispatch(fetchListData(listId)).catch(() => {});
    try {
      const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/lists/${listId}`);
      const listData = response?.data?.[0]?.list || null;

      setListDataFetched(listData);
    } catch (error) {
      console.error("Error fetching list:", error);
      setListDataFetched(null);
    }
  };

  const { lists, listData: selectedListData, loading } = useSelector((state) => state.lists.lists);
  const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false);

  const { user } = useSelector((state) => state.user);
  const { currentUser } = auth;
  const {
    audioUrl,
    setAudioUrl,
    isAudioPlaying,
    setIsAudioPlaying,
    audioTime,
    setAudioTime,
    audioRef,
    setAutoPlay,
  } = useAppContext();
  const { selectedCalls = [] } = useSelector((state) => state.calls.calls);
  const { company_config, loading: algoliaLoading } = useSelector((state) => state.algolia.algolia);

  const display_tz = company_config?.display_tz;
  const selectedcallsIds = selectedCalls?.map((call) => call.id) || [];

  const [inputName, setInputName] = useState("");
  const [isNewListPrivate, setIsNewListPrivate] = useState(false);
  const [listInputName, setListInputName] = useState("");
  const [openCall, setOpenCall] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [page, setPage] = useState(1);
  const [isNewListMenuOpen, setIsNewListMenuOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("active");
  const [editingList, setEditingList] = useState(null);

  const [nbCommentsByCall, setNbCommentsByCall] = useState({});
  const [isNbCommentsByCallLoading, setIsNbCommentsByCallLoading] = useState(false);

  const [filteredCalls, setFilteredCalls] = useState([]);
  const [nbDisplayedItems, setNbDisplayedItems] = useState(0);

  const notDeletedLists = lists.filter((list) => !list.is_deleted);
  const archivedLists = notDeletedLists.filter((list) => list.is_archived);
  const activeLists = notDeletedLists.filter((list) => !list.is_archived);
  const isArchivedDisplay = selectedTab === "archived";
  const displayList = isArchivedDisplay ? archivedLists : activeLists;

  const applyPageFiltersAndPagination = () => {
    let filteredCalls = listDataFetched?.calls?.filter((listCall) => listCall !== null) || [];

    // filter on searchText
    if (searchText && searchText.length > 0) {
      filteredCalls = filteredCalls.filter((call) => {
        const lowerSearchText = searchText.toLowerCase();

        if (call?.operator_name?.toLowerCase().includes(lowerSearchText)) {
          return true;
        }

        if (call?.id?.toLowerCase().includes(lowerSearchText)) {
          return true;
        }

        if (call?.department?.toLowerCase().includes(lowerSearchText)) {
          return true;
        }

        if (call?.client_number?.toLowerCase().includes(lowerSearchText)) {
          return true;
        }

        return false;
      });
    }

    // Filter on Grade
    if (
      individualGrades.min_individual_grade !== 0 ||
      individualGrades.max_individual_grade !== 10
    ) {
      filteredCalls = filteredCalls.filter((call) => {
        const callGrade = call?.analysis?.call_overall_grade || 0;
        return (
          callGrade >= individualGrades.min_individual_grade &&
          callGrade <= individualGrades.max_individual_grade
        );
      });
    }

    // Exclude N.A (call_overall_grade === -1) if requested
    if (individualGrades.exclude_na) {
      filteredCalls = filteredCalls.filter((call) => call?.analysis?.call_overall_grade !== -1);
    }

    // Filter on Date
    // let { startDate, endDate } = callsDateRange;

    // startDate = new Date(startDate).getTime();
    // endDate = new Date(endDate).getTime();

    // filteredCalls = filteredCalls.filter((call) => {
    //   const callDate = new Date(call?.call_timestamp).getTime();

    //   return callDate >= startDate && callDate <= endDate;
    // });

    const nbDisplayedItems = filteredCalls.length;

    // Sorting
    const isAscSorting = sortDescriptor.direction === "asc";

    const sortFunctionsBydescriptor = {
      call_timestamp: (a, b) => {
        const first = new Date(a.call_timestamp).getTime();
        const second = new Date(b.call_timestamp).getTime();
        const cmp = first < second ? -1 : first > second ? 1 : 0;

        return isAscSorting ? cmp : -cmp;
      },
      grade: (a, b) => {
        const first = a?.analysis?.call_overall_grade || 0;
        const second = b?.analysis?.call_overall_grade || 0;
        const cmp = first < second ? -1 : first > second ? 1 : 0;

        return isAscSorting ? cmp : -cmp;
      },
    };

    filteredCalls.sort(sortFunctionsBydescriptor[sortDescriptor.sortBy]);

    // Pagination
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = page * rowsPerPage;

    return {
      nbDisplayedItems,
      filteredCalls: filteredCalls.slice(startIndex, endIndex),
    };
  };

  useEffect(() => {
    if (!isListCallsDisplay) {
      return;
    }

    const { filteredCalls: filteredCallsEffect, nbDisplayedItems: nbDisplayedItemsEffect } =
      applyPageFiltersAndPagination();
    setFilteredCalls(filteredCallsEffect);
    setNbDisplayedItems(nbDisplayedItemsEffect);
  }, [
    listDataFetched,
    searchText,
    individualGrades,
    sortDescriptor,
    page,
    rowsPerPage,
    isListCallsDisplay,
  ]);

  const fetchNbCommentsByCall = async () => {
    setIsNbCommentsByCallLoading(true);
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/comments/get_nb_calls_comments`,
      {
        call_ids: filteredCalls.map((item) => item.id),
      }
    );
    const nbCommentsByCallsRetrieved = response?.data?.[0]?.comments || [];

    setNbCommentsByCall(nbCommentsByCallsRetrieved);
    setIsNbCommentsByCallLoading(false);
  };

  useEffect(() => {
    if (!isListCallsDisplay) {
      return;
    }

    if (filteredCalls.length <= 0) {
      return;
    }

    fetchNbCommentsByCall();
  }, [filteredCalls]);

  const totalPages = Math.ceil(nbDisplayedItems / rowsPerPage);

  const dispatchFetchLists = () => {
    dispatch(fetchLists());
  };

  useEffect(() => {
    dispatch(resetSelectedCalls());
  }, [dispatch]);

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }

    dispatchFetchLists();
  }, [user]);

  useEffect(() => {
    if (!currentUser || !isListCallsDisplay) {
      return;
    }

    onFetchListData();
  }, [user, listId, dispatch]);

  console.log({ user });

  const onCreateSmartList = async () => {
    if (querySmartList.length <= 0 || maxCallSmartList <= 0) {
      return;
    }
    setIsSmartListLoading(true);
    setSmartListCalls([]);
    setSmartListMeta(null);
    setSmartListLoadingStep({ step: "starting", message: t("Démarrage...") });

    try {
      // Get auth token for the request
      const token = await currentUser?.getIdToken();

      const response = await fetch(
        `${getConfig().API_URL}/audio/api/v1/ai_feature/smart_list_stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            text: querySmartList,
            start_date: smartListDateRange.startDate.toISOString().split("T")[0],
            end_date: smartListDateRange.endDate.toISOString().split("T")[0],
            list_limit: maxCallSmartList,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();

        // Process value even if done (final chunk may contain data)
        if (value) {
          buffer += decoder.decode(value, { stream: true });

          // Process complete events (separated by double newline)
          const events = buffer.split("\n\n");
          // Keep the last part in buffer (might be incomplete)
          buffer = events.pop() || "";

          events.forEach((event) => {
            const lines = event.split("\n").filter((line) => line.startsWith("data: "));
            lines.forEach((line) => {
              try {
                const data = JSON.parse(line.replace("data: ", ""));
                setSmartListLoadingStep(data);

                if (data.step === "complete") {
                  setSmartListCalls(data.calls || []);
                  setSmartListMeta(data.meta || null);
                } else if (data.step === "error") {
                  console.error("Smart list error:", data.message);
                }
              } catch (parseError) {
                console.warn("Failed to parse SSE data:", line);
              }
            });
          });
        }

        if (done) {
          // Process any remaining buffer
          if (buffer.trim()) {
            const lines = buffer.split("\n").filter((line) => line.startsWith("data: "));
            lines.forEach((line) => {
              try {
                const data = JSON.parse(line.replace("data: ", ""));
                setSmartListLoadingStep(data);

                if (data.step === "complete") {
                  setSmartListCalls(data.calls || []);
                  setSmartListMeta(data.meta || null);
                } else if (data.step === "error") {
                  console.error("Smart list error:", data.message);
                }
              } catch (parseError) {
                console.warn("Failed to parse SSE data (final):", line.substring(0, 100));
              }
            });
          }
          break;
        }
      }
    } catch (e) {
      console.log("Error fetching smart lists", e);
      setSmartListLoadingStep({ step: "error", message: e.message });
    } finally {
      setIsSmartListLoading(false);
    }
  };

  const onCreateList = async () => {
    if (inputName.length <= 0) {
      return;
    }

    const response = await myAxios.post(`${getConfig().API_URL}/audio/api/v1/lists`, {
      list_name: inputName,
      is_private: isNewListPrivate,
    });
    setIsNewListMenuOpen(false);
    setInputName("");
    setIsNewListPrivate(false);
    dispatchFetchLists();
  };

  const onArchiveList = async (listId) => {
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/lists/archive/${listId}`
    );
    dispatchFetchLists();
  };

  const onUnArchiveList = async (listId) => {
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/lists/unarchive/${listId}`
    );
    dispatchFetchLists();
  };

  const onDeleteList = async (listId) => {
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/lists/delete/${listId}`
    );
    dispatchFetchLists();
  };

  // Only the creator can switch a list between private and shared (enforced
  // server-side too).
  const onUpdateListVisibility = async (list, isPrivate) => {
    try {
      await myAxios.put(`${getConfig().API_URL}/audio/api/v1/lists/update_visibility/${list.id}`, {
        is_private: isPrivate,
      });
    } catch (error) {
      console.error("Error updating list visibility:", error);
    }
    dispatchFetchLists();
  };

  const onEditList = (list) => {
    setEditingList(list);
    setListInputName(list?.name);
  };

  const onCloseEditionModal = () => {
    setEditingList(null);
    setListInputName("");
  };

  const onSaveListName = async () => {
    console.log({ editingList });
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/lists/update_list_name/${editingList?.id}`,
      {
        new_name: listInputName,
      }
    );
    dispatchFetchLists();
    onCloseEditionModal();
  };

  const onDropdownAction = (list) => (e) => {
    const isArchiveAction = e === "archive";
    const isUnArchiveAction = e === "unarchive";
    const isDeleteAction = e === "delete";
    const isEditAction = e === "edit";
    const isMakePrivateAction = e === "make_private";
    const isMakeSharedAction = e === "make_shared";

    if (isUnArchiveAction) {
      onUnArchiveList(list.id);
    }

    if (isArchiveAction) {
      onArchiveList(list.id);
    }

    if (isEditAction) {
      onEditList(list);
    }

    if (isDeleteAction) {
      onDeleteList(list.id);
    }

    if (isMakePrivateAction) {
      onUpdateListVisibility(list, true);
    }

    if (isMakeSharedAction) {
      onUpdateListVisibility(list, false);
    }
  };

  const onSelectList = (list) => {
    navigate(`/calls/lists/${list.id}`);
  };

  const onAddSelection = (item) => () => {
    dispatch(addSelectedCall(item));
  };

  const onRemoveSelection = (item) => () => {
    dispatch(removeSelectedCall(item));
  };

  const onResetSelection = () => {
    dispatch(resetSelectedCalls());
  };

  const getIsSelectAllChecked = () => {
    if (nbDisplayedItems <= 0) {
      return false;
    }

    let hasNewCalls = false;
    let cpt = 0;

    while (!hasNewCalls && cpt < nbDisplayedItems) {
      const item = filteredCalls[cpt];
      if (!selectedcallsIds.includes(item.id)) {
        hasNewCalls = true;
      }
      cpt++;
    }

    return !hasNewCalls;
  };

  const onSelectAll = () => {
    const isAllSelected = getIsSelectAllChecked();
    dispatch(isAllSelected ? removeSelectedCalls(filteredCalls) : addSelectedCalls(filteredCalls));
  };

  const onOpenCall = (item) => {
    if (item && item?.analysis) {
      setSelectedItem(item);
      setPopupAudioUrl(item.audio_url);
      setPopupCallId(item.id);
      setPopupAudioTime(0);
      setSidebarAutoPlay(false);
      setOpenCall(true);
    }
  };

  const onCloseCallModal = () => {
    onFetchListData();
    setOpenCall(false);
    if (popupIsAudioPlaying) {
      setPopupIsAudioPlaying(false);

      setSidebarIsAudioPlaying(true);
      setSidebarAudioTime(popupAudioTime);
      setSidebarAudioSpeed(popupAudioSpeed);
      if (sidebarAudioRef && sidebarAudioRef.current) {
        sidebarAudioRef.current.currentTime = popupAudioTime;
        sidebarAudioRef.current.playbackRate = popupAudioSpeed;
        sidebarAudioRef.current.play();
      }
    }
  };

  const onOpenFullTranscript = () => {
    setIsTranscriptModalOpen(true);
    // onCloseCallModal();
  };

  const onSearchNumber = (clientNumber) => {
    onCloseCallModal();
    setPreventSearchReset(true);
    setSearchText(clientNumber);
    navigate("/calls");
  };

  const handleOnRowAction = (item) => () => {
    onOpenCall(item);
  };

  const onPlayPauseClick = (item) => (e) => {
    e.stopPropagation();

    if (!item || !item.audio_url) {
      return;
    }

    const isCurrentlyPlaying = item.audio_url === sidebarAudioUrl;
    if (!sidebarAudioRef.current) {
      return;
    }

    setSidebarAutoPlay(true);

    if (isCurrentlyPlaying) {
      if (sidebarIsAudioPlaying) {
        sidebarAudioRef.current.pause();
        setSidebarIsAudioPlaying(false);
      } else {
        sidebarAudioRef.current.play();
        setSidebarIsAudioPlaying(true);
      }
      return;
    }

    setSidebarAudioUrl(item.audio_url);
    setSidebarCallId(item.id);
    setSidebarAudioTime(0);
    sidebarAudioRef.current.currentTime = 0;
  };

  const renderCell = (item, columnKey) => {
    const isCurrentlyPlaying = item.audio_url === sidebarAudioUrl && sidebarIsAudioPlaying;
    const cellValue = item[columnKey];
    let { id: itemId } = item;
    let displayableId = itemId ? `...${itemId.substring(itemId.length - 6)}` : "";

    const isSelected = selectedcallsIds.includes(itemId);

    switch (columnKey) {
      case "checkbox":
        return (
          <Checkbox
            isSelected={isSelected}
            onValueChange={isSelected ? onRemoveSelection(item) : onAddSelection(item)}
          />
        );
      case "play_button":
        return (
          <div className="flex items-center space">
            <div
              className="w-auto h-auto flex items-center justify-center"
              onClick={onPlayPauseClick(item)}
              onKeyDown={onPlayPauseClick(item)}
            >
              {isCurrentlyPlaying ? (
                <AiFillPauseCircle
                  size={24}
                  color="#737373"
                  className="text-primary cursor-pointer"
                />
              ) : (
                <AiFillPlayCircle
                  size={24}
                  color="#737373"
                  className="text-primary cursor-pointer"
                />
              )}
            </div>
          </div>
        );
      case "call_id":
        return (
          <div className="flex items-center text-sm font-normal whitespace-normal">
            <CustomCopyToClipboard
              displayClassName="text-[#171717] min-w-[80px]"
              copyText={itemId}
              displayText={displayableId}
            />
            {isNbCommentsByCallLoading && (
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
              />
            )}
            {!isNbCommentsByCallLoading && nbCommentsByCall[itemId] > 0 && (
              <>
                <Tooltip id={`tooltip-${itemId}`} />
                <div
                  className="ml-[12px]"
                  data-tooltip-id={`tooltip-${itemId}`}
                  data-tooltip-content={`${nbCommentsByCall[itemId]} comment${
                    nbCommentsByCall[itemId] > 1 ? "s" : ""
                  }`}
                >
                  <BsChatLeftText
                    size={16}
                    color="#737373"
                  />
                </div>
              </>
            )}
          </div>
        );
      case "operator_name":
        return (
          <ACircleUserDisplay
            firstname={item.operator_name
              .split(" ")
              .map((item) => capitalizeFirstLetter(item))
              .join(" ")}
            lastname=""
            email=""
            hideUserPreview={isSmallScreen}
          />
        );
      case "call_timestamp":
        return item.call_timestamp ? (
          <div className="text-sm text-[#404040]/[0.6] font-normal flex flex-col space-y-1">
            <span>{format(new Date(item.call_timestamp), "MMM dd, yyyy")}</span>
            <span className="text-gray-400 text-xs">
              {format(new Date(item.call_timestamp), "HH:mm:ss")}
            </span>
          </div>
        ) : null;
      case "call_purpose":
        return (
          <div className="text-sm text-[#404040]/[0.6] font-normal">
            {item?.analysis?.call_purpose}
          </div>
        );
      case "grade":
        return (
          <div className="w-full flex justify-start items-center">
            <AStarGradeDisplay grade={item?.analysis?.call_overall_grade} />
          </div>
        );
      case "client_number":
        return (
          <div className="text-sm text-[#737373] font-normal flex items-center">
            <div>
              {item.incoming ? (
                <MdOutlinePhoneCallback
                  size={20}
                  color="#737373"
                  className="mr-2"
                />
              ) : (
                <MdOutlinePhoneForwarded
                  size={20}
                  color="#737373"
                  className="mr-2"
                />
              )}
            </div>
            {item?.client_number === "None" ? "Unknown" : item?.client_number}
          </div>
        );
      case "duration":
        return (
          <div className="text-sm text-[#404040]/[0.6] font-normal">
            {formatCallDuration(item?.call_duration_seconds)}
          </div>
        );
      default:
        return (
          <div className="text-sm text-[#404040]/[0.6] w-full float-left whitespace-normal">
            {cellValue}
          </div>
        );
    }
  };

  const renderTopContent = () => {
    const nbPageOptions = [12, 25, 50, 100];

    return (
      <div className="w-full py-2 px-3">
        <div className="flex items-center space-x-2 text-default-700">
          <span className="text-xs">{t("Showing")}</span>
          <Select
            aria-label="Select items per page"
            placeholder={t("misc.selectItemsPerPage")}
            selectedKeys={[rowsPerPage?.toString()]}
            defaultSelectedKeys={["12"]}
            className="max-w-[78px] p-0"
            color="primary"
            classNames={{
              trigger: "bg-white",
              selectorIcon: "w-2.5",
              label: "hidden",
              innerWrapper: "p-0",
              mainWrapper: "p-0 h-8",
              value: "text-sm leading-none text-primary font-medium font-PlusJakartaSans",
            }}
            size="sm"
            onChange={(e) => {
              const rows_per_page = e.target.value;
              if (rows_per_page === "") return;
              setRowsPerPage(rows_per_page);
              setPage(1);
              // handleOnPageChanged(1, rows_per_page);
            }}
            selectorIcon={<BiSolidUpArrow />}
          >
            {nbPageOptions.map((nbPageOption) => (
              <SelectItem
                key={`${nbPageOption}`}
                value={nbPageOption}
              >
                {`${nbPageOption}`}
              </SelectItem>
            ))}
          </Select>
          <span className="text-default-700 text-xs">{` ${t(
            "calls out of"
          )} ${nbDisplayedItems}`}</span>
        </div>
      </div>
    );
  };

  const renderPaginationItem = ({
    ref,
    key,
    value,
    isActive,
    onNext,
    onPrevious,
    setPage,
    className,
  }) => {
    if (value === PaginationItemType.NEXT) {
      return (
        <button
          type="button"
          key={key}
          className={cn(
            className,
            "items-center space-x-1 bg-transparent min-w-20 w-auto h-8 ml-3",
            page >= totalPages && "text-gray-200"
          )}
          onClick={onNext}
          disabled={page >= totalPages}
        >
          {t("Next")}{" "}
          <ChevronIcon
            className="w-8 rotate-180"
            size={18}
          />
        </button>
      );
    }
    if (value === PaginationItemType.PREV) {
      return (
        <button
          type="button"
          key={key}
          className={cn(
            className,
            "flex items-center space-x-1 bg-transparent min-w-20 w-auto h-8 mr-3",
            page <= 1 && "text-gray-200"
          )}
          onClick={onPrevious}
          disabled={page <= 1}
        >
          <ChevronIcon
            className="w-8"
            size={18}
          />{" "}
          {t("Prev")}
        </button>
      );
    }
    if (value === PaginationItemType.DOTS) {
      return (
        <button
          type="button"
          key={key}
          className={className}
        >
          ...
        </button>
      );
    }
    // cursor is the default item
    return (
      <button
        type="button"
        key={key}
        ref={ref}
        className={cn(className)}
        onClick={() => {
          setPage(value);
        }}
      >
        {value}
      </button>
    );
  };

  const renderBottomContent = () => {
    return (
      <div className="flex justify-center items-center pt-4 px-2">
        {loading && (
          <div className="w-full min-h-full flex items-center justify-center">
            <CircularProgress
              aria-label="Loading..."
              color="primary"
              label={t("Loading...")}
            />
          </div>
        )}
        {!loading && (
          <Pagination
            showControls
            classNames={{
              cursor: "rounded-full",
            }}
            color="primary"
            page={page}
            total={totalPages}
            variant="light"
            onChange={(new_page) => {
              setPage(new_page);
            }}
            renderItem={renderPaginationItem}
          />
        )}
      </div>
    );
  };

  const renderTableEmptyState = () => (
    <div className="w-full h-full flex-1 flex flex-col items-center justify-center">
      <BsDatabaseFill className="text-gray-300 w-12 h-12" />
      <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
    </div>
  );

  const renderListCalls = () => {
    return (
      <div>
        {/* List detail header */}
        {listDataFetched && (
          <div className="flex items-center gap-3 px-3 pb-3">
            {listDataFetched.is_private && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[#404040] text-[10px] font-medium">
                <BsLockFill size={10} />
                {t("Privée")}
              </span>
            )}
            {listDataFetched.is_smart_list && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                <AISuggestionIcon size={12} />
                Smart
              </span>
            )}
            {listDataFetched.creator_name && (
              <span className="text-sm text-[#404040]/[0.5]">
                {t("Créée par")} {listDataFetched.creator_name}
              </span>
            )}
          </div>
        )}
        {renderTopContent()}
        {selectedCalls.length > 0 && (
          <div className="flex items-center space-x-6 pt-3 pl-3">
            <div
              className="cursor-pointer text-sm text-blue-700"
              onClick={onResetSelection}
              onKeyDown={onResetSelection}
            >
              {`Unselect All (${selectedCalls.length})`}
            </div>
          </div>
        )}
        {filteredCalls?.length > 0 && (
          <GenericTable
            items={filteredCalls}
            titles={filteredTableColumns}
            getItemContent={renderCell}
            onRowClick={handleOnRowAction}
            sortDescriptor={sortDescriptor}
            setSortDescriptor={setSortDescriptor}
            // displaySelectAll
            selectAllAction={onSelectAll}
            isSelectAllChecked={getIsSelectAllChecked()}
          />
        )}
        {filteredCalls?.length <= 0 && !loading && renderTableEmptyState()}
        {renderBottomContent()}
        {isTranscriptModalOpen && (
          <Modal
            isOpen={isTranscriptModalOpen}
            showFooter={false}
            size="4xl"
            handleOnClose={() => setIsTranscriptModalOpen(false)}
          >
            <LiveContentModalContent
              item={selectedItem}
              disablePlayer
              handleOnClose={() => setIsTranscriptModalOpen(false)}
            />
          </Modal>
        )}
      </div>
    );
  };

  const renderLists = () => {
    if (loading) {
      return (
        <div className="w-full min-h-full flex items-center justify-center">
          <CircularProgress
            aria-label="Loading..."
            color="primary"
            label={t("Loading...")}
          />
        </div>
      );
    }

    if (displayList.length === 0) {
      return (
        <div className="w-full h-full flex-1 flex flex-col items-center justify-center">
          <BsDatabaseFill className="text-gray-300 w-12 h-12" />
          <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
        </div>
      );
    }

    return displayList.map((list) => {
      return (
        <Card
          key={list.id}
          isPressable
          onPress={() => onSelectList(list)}
          className="shadow-custom-light rounded-md hover:bg-[#F5F5F5]"
        >
          <CardBody>
            <div className="flex items-center justify-center h-full">
              <div className="shrink-0">
                <MdOutlineFormatListBulleted size={28} />
              </div>
              <div className="ml-3 grow">
                <div className="flex flex-col">
                  <span className="text-md text-[#171717] flex items-center gap-1.5">
                    {list.is_private && (
                      <span
                        className="shrink-0 text-[#737373]"
                        title={t("Liste privée : visible uniquement par vous")}
                        aria-label={t("Liste privée : visible uniquement par vous")}
                      >
                        <BsLockFill size={12} />
                      </span>
                    )}
                    <span>{list.name}</span>
                  </span>
                  <span className="text-sm text-[#404040]/[0.6]">
                    {format(parseISO(list.created_at), "dd/MM/yyyy")}
                  </span>
                </div>
              </div>
              <div className="flex">
                <div className="flex items-center mr-[12px] rounded-full text-center px-2 py-1 border-1 border-solid border-[#E5E5E5]">
                  <span className="text-sm text-[#171717]">{list.nb_calls}</span>
                </div>
              </div>

              <Dropdown>
                <DropdownTrigger>
                  <div className="cursor-pointer">
                    <BsThreeDotsVertical size={18} />
                  </div>
                </DropdownTrigger>
                <DropdownMenu
                  variant="faded"
                  aria-label="Lists Actions"
                  onAction={onDropdownAction(list)}
                >
                  {!isArchivedDisplay && <DropdownItem key="edit">Edit</DropdownItem>}
                  {!isArchivedDisplay && <DropdownItem key="archive">Archive</DropdownItem>}
                  {list.is_owner && !list.is_private && (
                    <DropdownItem key="make_private">{t("Rendre privée")}</DropdownItem>
                  )}
                  {list.is_owner && list.is_private && (
                    <DropdownItem key="make_shared">{t("Partager avec l'équipe")}</DropdownItem>
                  )}
                  {isArchivedDisplay && <DropdownItem key="unarchive">Restore</DropdownItem>}
                  <DropdownItem
                    key="delete"
                    className="text-danger"
                    color="danger"
                  >
                    Delete
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </CardBody>
        </Card>
      );
    });
  };

  const renderCreateListBtn = () => {
    return (
      <Button
        radius="sm"
        color="primary"
        onPress={() => setIsNewListMenuOpen(!isNewListMenuOpen)}
      >
        <div>{t("Create new list")}</div>
        <GoPlus
          size={20}
          className="ml-1"
        />
      </Button>
    );
  };

  const renderPrivateToggle = (isPrivate, setIsPrivate, className = "") => (
    <Checkbox
      size="sm"
      className={className}
      isSelected={isPrivate}
      onValueChange={setIsPrivate}
    >
      <span className="flex items-center gap-1 text-sm text-[#33403F]">
        <BsLockFill size={11} />
        {t("Liste privée")}
      </span>
      <span className="block text-xs text-[#737373]">{t("Visible uniquement par vous")}</span>
    </Checkbox>
  );

  const renderCreateListMenu = () => {
    return (
      <div className="mt-1.5 bg-white rounded-lg p-2 text-sm drop-shadow-sm">
        <div className="flex items-center text-[#33403F] mb-2">
          <BsPlus size={24} />
          <span className="whitespace-nowrap ml-2 mr-4">{t("Create new list")}</span>
          <BsChevronDown
            style={{ marginLeft: "auto" }}
            size={16}
          />
        </div>
        <div className="flex items-center rounded-full border-2 border-[#E5E5E5] border-solid bg-white pl-2">
          <input
            className="outline-none	border-0"
            placeholder={t("common.name")}
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
          />
          <div
            onClick={onCreateList}
            onKeyDown={onCreateList}
            className="cursor-pointer rounded-full border-2 border-[#E5E5E5] border-solid px-3 py-2"
            style={{ margin: "-2px" }}
          >
            {t("Create")}
          </div>
        </div>
        {renderPrivateToggle(isNewListPrivate, setIsNewListPrivate, "mt-2 px-1")}
      </div>
    );
  };

  const renderCreateSmartListBtn = () => {
    return (
      <Button
        radius="sm"
        color="primary"
        className="mr-4"
        onPress={() => setSmartNewListMenuOpen(true)}
      >
        <div className="flex items-center px-4">
          <div className="text-white whitespace-nowrap">{t("Smart list (Beta)")}</div>
          <AISuggestionIcon
            className="ml-1 shrink-0"
            size={20}
          />
        </div>
      </Button>
    );
  };

  // <div
  //   radius="sm"
  //   color="primary"
  //   className="bg-primary px-4 py-2 rounded mr-4 text-sm flex items-center cursor-pointer"
  //   onClick={() => setSmartNewListMenuOpen(true)}
  //   onKeyDown={() => setSmartNewListMenuOpen(true)}
  // >
  //   <div className="text-white whitespace-nowrap">{t("Smart list (Beta)")}</div>
  //   <AISuggestionIcon
  //     className="ml-1 shrink-0"
  //     size={20}
  //   />
  // </div>

  const onCloseSmartListModal = () => {
    setSmartNewListMenuOpen(false);
    // Reset state when closing
    setSmartListCalls([]);
    setSmartListMeta(null);
    setSmartListLoadingStep(null);
    setExcludedSmartListCallIds([]);
    setSmartListName("");
    setIsSmartListPrivate(false);
  };

  const handleToggleExcludeCall = (callId) => {
    setExcludedSmartListCallIds((prev) => {
      if (prev.includes(callId)) {
        return prev.filter((id) => id !== callId);
      }
      return [...prev, callId];
    });
  };

  const onCreateListFromSmartList = async () => {
    if (!smartListName.trim()) {
      return;
    }

    const includedCallIds = smartListCalls
      .map((call) => call.id || call.objectID)
      .filter((id) => !excludedSmartListCallIds.includes(id));

    if (includedCallIds.length === 0) {
      return;
    }

    setIsCreatingSmartList(true);

    try {
      const token = await currentUser.getIdToken();
      await myAxios.post(
        `${getConfig().API_URL}/audio/api/v1/lists`,
        {
          list_name: smartListName.trim(),
          call_ids: includedCallIds,
          is_smart_list: true,
          smart_list_query: querySmartList,
          is_private: isSmartListPrivate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Refresh lists
      dispatch(fetchLists());
      // Close modal
      onCloseSmartListModal();
    } catch (error) {
      console.error("Error creating list from smart list:", error);
    } finally {
      setIsCreatingSmartList(false);
    }
  };

  const renderSmartListModalContent = () => {
    return (
      <div className="bg-white p-6">
        {/* Header */}
        <div className="flex items-center text-[#33403F] mb-6">
          <BsPlus size={28} />
          <span className="whitespace-nowrap ml-2 text-xl font-semibold">{t("Smart List")}</span>
          <Tooltip
            id="smart-list-help"
            className=" z-20"
          />
          <div
            className="ml-2 cursor-help text-gray-400 hover:text-gray-600"
            data-tooltip-id="smart-list-help"
            data-tooltip-class-name="z-20"
            data-tooltip-html={`<div style="max-width: 300px;"><strong>${t(
              "Comment ça marche"
            )}</strong><br/><br/>${t(
              "Décrivez les appels que vous recherchez en langage naturel. L'IA analysera les métadonnées et les transcriptions pour trouver les appels correspondants."
            )}<br/><br/><span style="font-size: 12px; color: #9CA3AF;">${t(
              "Les résultats dépendent des données disponibles (transcriptions, analyses). Les requêtes sémantiques complexes peuvent prendre plus de temps."
            )}</span></div>`}
          >
            <BsInfoCircle size={18} />
          </div>
        </div>

        {/* Query Input Section - hidden when results are shown */}
        {(smartListCalls.length === 0 || isSmartListLoading) && (
          <>
            {/* Date Range Picker */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-gray-600">{t("Période")} :</span>
              <CalendarDropdown
                months={1}
                showShortcuts={false}
                dateRangeValue={smartListDateRange}
                onDateRangeChange={setSmartListDateRange}
                triggerClassName="border border-[#E5E5E5] text-sm"
              />
              <span className="text-sm text-gray-500">
                {format(smartListDateRange.startDate, "dd/MM/yyyy")} -{" "}
                {format(smartListDateRange.endDate, "dd/MM/yyyy")}
              </span>
            </div>

            {/* Query Input */}
            <div className="flex items-start rounded-lg border border-[#E5E5E5] bg-white p-3 mb-4">
              <textarea
                className="outline-none border-0 rounded-lg w-full resize-none min-h-[80px] text-base"
                placeholder={t("Décrivez les appels que vous recherchez...")}
                value={querySmartList}
                onChange={(e) => setQuerySmartList(e.target.value)}
                rows={3}
              />
            </div>

            {/* Example Queries */}
            <div className="mb-4">
              <span className="text-xs text-gray-500 mb-2 block">{t("Exemples")} :</span>
              <div className="flex flex-wrap gap-2">
                {[
                  "Appels avec des propositions additionnelles",
                  "Appels de {nom de l'agent} sans résolution du problème",
                  "Appels mentionnant un concurrent",
                  "Appels mentionnant {nom d'un concurrent, produit, contrat...}",
                  "Appels avec une proposition commerciale refusée",
                ].map((example) => (
                  <button
                    key={example}
                    type="button"
                    className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 transition-colors"
                    onClick={() => setQuerySmartList(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end mb-4">
              <Button
                className="text-sm"
                size="md"
                color="primary"
                radius="full"
                onPress={onCreateSmartList}
                isDisabled={isSmartListLoading || querySmartList.length === 0}
              >
                <div>{t("Rechercher")}</div>
              </Button>
            </div>
          </>
        )}

        {/* Loading State with Steps */}
        {isSmartListLoading && smartListLoadingStep && (
          <div className="flex flex-col w-full py-6 px-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-4">
              <CircularProgress
                aria-label="Loading..."
                color="primary"
                size="sm"
                className="mr-3"
              />
              <span className="text-sm font-medium text-gray-700">
                {smartListLoadingStep.message || t("Traitement en cours...")}
              </span>
            </div>

            {/* Step indicators */}
            <div className="space-y-2 text-sm">
              {/* Step 1: Context */}
              <div
                className={`flex items-center ${
                  [
                    "context",
                    "analyzing",
                    "analyzed",
                    "fetching",
                    "fetched",
                    "filtering_start",
                    "filtering_batches",
                    "filtering_complete",
                    "complete",
                  ].includes(smartListLoadingStep.step)
                    ? "text-green-600"
                    : "text-gray-400"
                }`}
              >
                <span className="w-5 h-5 mr-2 flex items-center justify-center">
                  {[
                    "analyzing",
                    "analyzed",
                    "fetching",
                    "fetched",
                    "filtering_start",
                    "filtering_batches",
                    "filtering_complete",
                    "complete",
                  ].includes(smartListLoadingStep.step)
                    ? "✓"
                    : "○"}
                </span>
                {t("Construction du contexte")}
              </div>

              {/* Step 2: Analyzing */}
              <div
                className={`flex items-center ${
                  [
                    "analyzed",
                    "fetching",
                    "fetched",
                    "filtering_start",
                    "filtering_batches",
                    "filtering_complete",
                    "complete",
                  ].includes(smartListLoadingStep.step)
                    ? "text-green-600"
                    : smartListLoadingStep.step === "analyzing"
                    ? "text-primary font-medium"
                    : "text-gray-400"
                }`}
              >
                <span className="w-5 h-5 mr-2 flex items-center justify-center">
                  {[
                    "fetching",
                    "fetched",
                    "filtering_start",
                    "filtering_batches",
                    "filtering_complete",
                    "complete",
                  ].includes(smartListLoadingStep.step)
                    ? "✓"
                    : smartListLoadingStep.step === "analyzing"
                    ? "●"
                    : "○"}
                </span>
                {t("Analyse de la requête")}
              </div>

              {/* Step 3: Fetching */}
              <div
                className={`flex items-center ${
                  [
                    "fetched",
                    "filtering_start",
                    "filtering_batches",
                    "filtering_complete",
                    "complete",
                  ].includes(smartListLoadingStep.step)
                    ? "text-green-600"
                    : smartListLoadingStep.step === "fetching"
                    ? "text-primary font-medium"
                    : "text-gray-400"
                }`}
              >
                <span className="w-5 h-5 mr-2 flex items-center justify-center">
                  {[
                    "filtering_start",
                    "filtering_batches",
                    "filtering_complete",
                    "complete",
                  ].includes(smartListLoadingStep.step)
                    ? "✓"
                    : smartListLoadingStep.step === "fetching"
                    ? "●"
                    : "○"}
                </span>
                {t("Récupération des appels")}
                {smartListLoadingStep.step === "fetched" && smartListLoadingStep.count && (
                  <span className="ml-2 text-gray-500">
                    ({smartListLoadingStep.count} {t("trouvés")})
                  </span>
                )}
              </div>

              {/* Step 4: Semantic filtering (conditional) */}
              {(smartListLoadingStep.step === "filtering_start" ||
                smartListLoadingStep.step === "filtering_batches" ||
                smartListLoadingStep.step === "filtering_complete" ||
                (smartListLoadingStep.step === "analyzed" &&
                  smartListLoadingStep.analysis?.has_semantic_filter)) && (
                <div
                  className={`flex items-center ${
                    smartListLoadingStep.step === "filtering_complete" ||
                    smartListLoadingStep.step === "complete"
                      ? "text-green-600"
                      : ["filtering_start", "filtering_batches"].includes(smartListLoadingStep.step)
                      ? "text-primary font-medium"
                      : "text-gray-400"
                  }`}
                >
                  <span className="w-5 h-5 mr-2 flex items-center justify-center">
                    {smartListLoadingStep.step === "filtering_complete" ||
                    smartListLoadingStep.step === "complete"
                      ? "✓"
                      : ["filtering_start", "filtering_batches"].includes(smartListLoadingStep.step)
                      ? "●"
                      : "○"}
                  </span>
                  {t("Filtrage sémantique")}
                  {smartListLoadingStep.step === "filtering_batches" &&
                    smartListLoadingStep.total_batches && (
                      <span className="ml-2 text-gray-500">
                        ({smartListLoadingStep.total_batches} {t("lots")})
                      </span>
                    )}
                  {smartListLoadingStep.step === "filtering_complete" &&
                    smartListLoadingStep.matched_count !== undefined && (
                      <span className="ml-2 text-gray-500">
                        ({smartListLoadingStep.matched_count} {t("correspondances")})
                      </span>
                    )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results Metadata */}
        {/* {smartListMeta && !isSmartListLoading && (
          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 mb-4">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <span>
                {t("Index")}: <strong>{smartListMeta.index_used}</strong>
              </span>
              <span>
                {t("Found")}: <strong>{smartListMeta.total_from_algolia}</strong> {t("calls")}
              </span>
              {smartListMeta.matched_count !== null && (
                <span>
                  {t("After filtering")}: <strong>{smartListMeta.matched_count}</strong>
                </span>
              )}
            </div>
            {smartListMeta.semantic_criteria && (
              <div className="mt-2 text-gray-500 italic">
                {t("Semantic filter")}: {smartListMeta.semantic_criteria}
              </div>
            )}
          </div>
        )} */}

        {/* Results List */}
        {smartListCalls.length > 0 && !isSmartListLoading && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {t("Appels sélectionnés")}:{" "}
                  {smartListCalls.length - excludedSmartListCallIds.length} /{" "}
                  {smartListCalls.length}
                </span>
                <Button
                  size="sm"
                  variant="light"
                  color="default"
                  onPress={() => {
                    setSmartListCalls([]);
                    setSmartListMeta(null);
                    setExcludedSmartListCallIds([]);
                  }}
                >
                  {t("Nouvelle recherche")}
                </Button>
              </div>
              {excludedSmartListCallIds.length > 0 && (
                <Button
                  size="sm"
                  variant="light"
                  color="primary"
                  onPress={() => setExcludedSmartListCallIds([])}
                >
                  {t("Tout sélectionner")}
                </Button>
              )}
            </div>
            <SmartListCallPreview
              calls={smartListCalls}
              excludedCallIds={excludedSmartListCallIds}
              onExcludeToggle={handleToggleExcludeCall}
              onOpenCall={(item) => {
                // Open call modal without closing smart list modal
                onOpenCall(item);
              }}
              display_tz={display_tz}
            />

            {/* Create List Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-primary"
                  placeholder={t("Nom de la liste...")}
                  value={smartListName}
                  onChange={(e) => setSmartListName(e.target.value)}
                />
                <Button
                  color="primary"
                  radius="full"
                  onPress={onCreateListFromSmartList}
                  isDisabled={
                    !smartListName.trim() ||
                    isCreatingSmartList ||
                    smartListCalls.length - excludedSmartListCallIds.length === 0
                  }
                  isLoading={isCreatingSmartList}
                >
                  {t("Créer la liste")}
                </Button>
              </div>
              {renderPrivateToggle(isSmartListPrivate, setIsSmartListPrivate, "mt-2")}
              {smartListCalls.length - excludedSmartListCallIds.length === 0 && (
                <p className="text-sm text-red-500 mt-2">
                  {t("Vous devez inclure au moins un appel")}
                </p>
              )}
            </div>
          </>
        )}

        {/* No Results */}
        {smartListCalls.length === 0 && smartListMeta && !isSmartListLoading && (
          <div className="text-center text-gray-500 py-8">
            {t("Aucun appel trouvé correspondant à vos critères")}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 pb-32 max-w-full">
      {!isListCallsDisplay && (
        <div className="flex w-full items-center justify-between mb-12 mt-8">
          <Tabs
            aria-label="Options"
            variant="underlined"
            onSelectionChange={(e) => setSelectedTab(e)}
            selectedKey={selectedTab}
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-divider mr-[24px]",
              cursor: "w-full bg-[#4318FF]",
              tab: "max-w-fit px-0 h-12",
              tabContent: "group-data-[selected=true]:text-[#4318FF]",
            }}
            fullWidth
          >
            <Tab
              key="active"
              title={
                <div className="flex items-center space-x-3 px-3">
                  {/* <BsBookmarkFill size={22} /> */}
                  <span className="text-[16px] font-semibold">{t("À traiter")}</span>
                  <span
                    className={`rounded-full text-[10px] px-[8px] py-[4px] ${
                      !isArchivedDisplay
                        ? "bg-primary text-white"
                        : "bg-white text-#171717 border-1 border-solid border-[#E5E5E5]"
                    }`}
                  >
                    {activeLists.length}
                  </span>
                </div>
              }
            />
            <Tab
              key="archived"
              title={
                <div className="flex items-center space-x-3 px-3">
                  {/* <BsCheckSquareFill size={22} /> */}
                  <span className="text-[16px] font-semibold">{t("Archivées")}</span>
                  <span
                    className={`rounded-full text-[10px] px-[8px] py-[4px] ${
                      isArchivedDisplay
                        ? "bg-primary text-white"
                        : "bg-white text-#171717 border-1 border-solid border-[#E5E5E5]"
                    }`}
                  >
                    {archivedLists.length}
                  </span>
                </div>
              }
            />
          </Tabs>
          {renderCreateSmartListBtn()}
          <MFloatingComponent
            targetElement={renderCreateListBtn()}
            floatingElement={renderCreateListMenu()}
            isFloatingDisplayed={isNewListMenuOpen}
            setIsFloatingDisplayed={setIsNewListMenuOpen}
            side="right"
          />
        </div>
      )}
      {!isListCallsDisplay && (
        <div className="grid gap-[16px] grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {renderLists()}
        </div>
      )}
      {isListCallsDisplay && renderListCalls()}
      {editingList !== null && (
        <Modal
          isOpen={editingList !== null}
          showFooter={false}
          size="sm"
          handleOnClose={onCloseEditionModal}
        >
          <div className="bg-white p-[30px] flex flex-col">
            <span className="text-bold text-[22px]">{t("Edit list name")}</span>
            <input
              className="outline-none	border-0 my-[20px]"
              placeholder={t("common.name")}
              value={listInputName}
              onChange={(e) => setListInputName(e.target.value)}
            />
            <Button
              className="ml-2 text-sm"
              size="sm"
              color="primary"
              radius="full"
              onPress={() => onSaveListName()}
            >
              <div>{t("Save")}</div>
            </Button>
          </div>
        </Modal>
      )}
      {isSmartNewListMenuOpen && (
        <Modal
          isOpen={isSmartNewListMenuOpen}
          showFooter={false}
          size="4xl"
          handleOnClose={onCloseSmartListModal}
        >
          {renderSmartListModalContent()}
        </Modal>
      )}
      {openCall && (
        <Modal
          isOpen={openCall}
          showFooter={false}
          size="full"
          handleOnClose={onCloseCallModal}
          displayCloseButton={false}
        >
          <CallModal
            item={selectedItem}
            handleOnClose={onCloseCallModal}
            onSearchNumber={onSearchNumber}
            onOpenFullTranscript={onOpenFullTranscript}
            display_tz={display_tz}
          />
        </Modal>
      )}
    </div>
  );
};
export default Lists;
